import { Navigate } from 'react-router-dom';
import { Can } from 'context';
import { useEffect, useState, useMemo } from 'react';
import Card from '@mui/material/Card';
import Icon from '@mui/material/Icon';

import { getSessions } from 'services/sessions';
import sessionsColumnData from 'data/sessionsColumnData';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import usePersistedFilters, { usePageFilters } from 'hooks/usePersistedFilters';
import UnifiedFilterBar from 'components/UnifiedFilterBar';
import { getMetricGames, getMetricCountries } from 'services/metrics';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'components/DashboardNavbar';
import Footer from 'examples/Footer';
import DataTable from 'components/DataTablePage/components/DataTable';

const DEFAULT_PAGE_FILTERS = {
  search: '',
  gameType: '',
  country: ''
};

function Sessions() {
  // Shared filters (date, entity)
  const { filters: sharedFilters, setDateRange, setEntityFilters } = usePersistedFilters();

  // Page-specific filters
  const { pageFilters, setPageFilters, clearPageFilters } = usePageFilters('sessions', DEFAULT_PAGE_FILTERS);

  // Async options for game and country filters
  const [gameOptions, setGameOptions] = useState([]);
  const [countryOptions, setCountryOptions] = useState([]);

  // Load game and country options
  useEffect(() => {
    const loadOptions = async () => {
      try {
        const [gamesRes, countriesRes] = await Promise.all([getMetricGames(), getMetricCountries()]);

        if (gamesRes?.status === 200 || gamesRes?.status === 201) {
          const games = (gamesRes.data ?? []).map((name) => ({
            value: String(name),
            label: String(name)
          }));
          setGameOptions(games);
        }

        if (countriesRes?.status === 200 || countriesRes?.status === 201) {
          const countries = (countriesRes.data ?? []).map((name) => ({
            value: String(name),
            label: String(name)
          }));
          setCountryOptions(countries);
        }
      } catch (err) {
        console.error('Error loading filter options:', err);
      }
    };

    loadOptions();
  }, []);

  // Combined filters for API calls
  const combinedFilters = useMemo(
    () => ({
      startDate: sharedFilters.startDate ? sharedFilters.startDate.toISOString() : '',
      endDate: sharedFilters.endDate ? sharedFilters.endDate.toISOString() : '',
      clientId: sharedFilters.clientIds?.[0] || '',
      operatorId: sharedFilters.operatorIds?.[0] || '',
      brandId: sharedFilters.brandIds?.[0] || '',
      search: pageFilters.search,
      gameType: pageFilters.gameType,
      country: pageFilters.country
    }),
    [sharedFilters, pageFilters]
  );

  const queryKey = ['sessions', combinedFilters];

  // Page-specific filter configuration
  const pageFilterConfig = useMemo(
    () => [
      {
        key: 'gameType',
        label: 'Game',
        type: 'async-select',
        loadedOptions: gameOptions
      },
      {
        key: 'country',
        label: 'Country',
        type: 'async-select',
        loadedOptions: countryOptions
      },
      {
        key: 'search',
        label: 'Player Search',
        type: 'text'
      }
    ],
    [gameOptions, countryOptions]
  );

  return (
    <>
      <Can I='read' a='session'>
        <DashboardLayout>
          <DashboardNavbar />
          <MDBox pt={6} pb={3}>
            {/* Filter Bar - Above the table */}
            <UnifiedFilterBar
              filters={sharedFilters}
              setDateRange={setDateRange}
              setEntityFilters={setEntityFilters}
              pageFilters={pageFilters}
              setPageFilters={setPageFilters}
              pageFilterConfig={pageFilterConfig}
              onClearPageFilters={clearPageFilters}
            />

            {/* Data Table */}
            <Card>
              <MDBox
                sx={{
                  height: '3px',
                  background: 'linear-gradient(90deg, #e2001b, #ff4f69, transparent 80%)',
                  borderRadius: '12px 12px 0 0'
                }}
              />
              <MDBox p={3} lineHeight={1} display='flex' alignItems='center' gap={1.5}>
                <MDBox
                  sx={{
                    width: 38, height: 38, borderRadius: '10px',
                    background: '#ffb74d18', border: '1px solid #ffb74d40',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
                  }}
                >
                  <Icon sx={{ fontSize: '1.25rem', color: '#ffb74d' }}>schedule</Icon>
                </MDBox>
                <MDTypography variant='h5' fontWeight='medium'>
                  Sessions
                </MDTypography>
              </MDBox>
              <DataTable
                canFilter={false}
                fetchData={getSessions}
                queryKey={queryKey}
                columnData={sessionsColumnData}
                object='session'
                noActions
                filters={combinedFilters}
              />
            </Card>
          </MDBox>
          <Footer />
        </DashboardLayout>
      </Can>
      <Can not I='read' a='session'>
        <Navigate to='/dashboard' replace />
      </Can>
    </>
  );
}

export default Sessions;
